import { RouteFallback } from '@/components/ap/skeletons';
import { ZoomableImageLayer } from '@/components/ap/zoom';
import { SidebarInset, SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar';
import { isDemoBuild } from '@/config/appConfig';
import CustomAuthenticated from '@/contexts/customAuthenticated';
import { useCurrentUser, useEnabledFeatures } from '@/hooks/dataHelpers/useCurrentUser';
import { useEffectiveDirection } from '@/lib/hooks/useEffectiveDirection';
import { useSeedDisplayPrefsFromGrant } from '@/lib/hooks/useSeedDisplayPrefsFromGrant';
import { checkIfUnsupportedApp, getAppType } from '@/lib/offline/OfflineAppHelper';
import { selectEmbedded, useCommonStore } from '@/store/commonStore';
import { CatchAllNavigate } from '@refinedev/react-router';
import { Direction } from 'radix-ui';
import { Suspense, useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { AppNotice } from './AppNotice';
import { AppSidebar } from './AppSidebar';
import { DemoBar } from './demoSwitcher/DemoBar';
import { HighlightAnnouncement } from './HighlightAnnouncement';

/**
 * APL-11850 (Slice 5, revised fix round G) — the v4 authenticated layout,
 * successor to AppV1's `AppLayout` (ThemedLayoutV2). Shares the SAME auth
 * guard, resources and Casbin gating as v1 (spec § Routing — Permission
 * gate); only the view layer differs. Unmigrated pages render inside this
 * shell via the `v4/routes.tsx` V1 fallback.
 *
 * No top bar (fix round G): the v4 shell matches v3's IA — user menu, theme
 * switcher and the "New design" toggle all live in the sidebar FOOTER
 * (bottom-left, AppSidebar). On desktop, main content starts at the top of
 * the viewport; the only exception is a mobile-only (<768px) trigger bar,
 * required because the shadcn Sidebar renders as a closed Sheet there
 * (fix round J — shell rebuilt on shadcn's official Sidebar family:
 * SidebarProvider here, Sidebar/SidebarInset layout, Cmd/Ctrl+B toggle).
 *
 * Outlet scoping (IK1, 7.3 — the fix-round-G migratedPaths/`.force-light`
 * fallback branch is RETIRED): every in-shell route is migrated, so the
 * outlet renders bare with full v4 theming. v1 remnants INSIDE migrated pages
 * (embedded student list, island seams) re-create the light scope locally
 * via `V1IslandScope` — that component owns the `.force-light` recipe now.
 *
 * AppLayout globals all carried now: DemoHeader → DemoBar (demo-switcher
 * run); AppNotice + HighlightAnnouncement → native ports (VG1-gaps run) —
 * rendered above the Outlet, exactly like v3 mounted them above
 * ThemedLayoutV2.
 *
 * Direction (RTL run): `dir` + a `Direction.Provider` (so Select/ToggleGroup's
 * `useDirection()` follow suit even across portals) apply on SidebarProvider —
 * the v4 root, never <html>.
 */
export const AppShell = () => {
  const currentUser = useCurrentUser();
  const appType = getAppType();
  const enabledFeatures = useEnabledFeatures();
  const isUnsupportedApp = checkIfUnsupportedApp(appType, enabledFeatures, currentUser);
  const navigate = useNavigate();
  const direction = useEffectiveDirection();
  const embedded = useCommonStore(selectEmbedded);

  // v3 AppV1 HighlightAnnouncement mount gate, verbatim.
  const showHighlightAnnouncement =
    !embedded &&
    appType === 'web' &&
    (enabledFeatures?.enable_whats_new || enabledFeatures?.enable_announcements) &&
    (currentUser?.role === 'admin' || currentUser?.role === 'teacher');

  useEffect(() => {
    if (isUnsupportedApp) {
      navigate('/unsupported');
    }
  }, [isUnsupportedApp, navigate]);

  useSeedDisplayPrefsFromGrant();

  return (
    <CustomAuthenticated key="authenticated-inner" fallback={<CatchAllNavigate to="/auth/login" />}>
      {/* The explicit bg-background/text-foreground matter: <body> has no v4
          background, so dark mode must paint the viewport here. */}
      <Direction.Provider dir={direction}>
        <SidebarProvider
          dir={direction}
          className="bg-background text-foreground"
          data-testid="v4-app-shell"
        >
          <AppSidebar />
          {/* bg-ap-canvas (LU2): the Luma layered look — muted page canvas,
              kit surfaces float on it with bg-card. Fallback routes are
              unaffected (the force-light wrapper below paints bg-background
              white over the whole content column). */}
          <SidebarInset className="min-w-0 bg-ap-canvas">
            {/* Mobile-only bar: on <768px the sidebar is a closed Sheet, so the
                open affordance must live in the content area. */}
            <div className="h-12 shrink-0 items-center border-b border-border px-2 max-md:flex md:hidden">
              <SidebarTrigger />
            </div>
            {/* AppNotice above DemoBar — v3 AppV1 order (notice, then demo
                header). Shell chrome: renders on both branches below. */}
            <AppNotice />
            {showHighlightAnnouncement && <HighlightAnnouncement />}
            {/* Demo bar: shell chrome — renders on BOTH branches below
                (migrated + v1-fallback), so it lives here, outside the
                isMigratedRoute split. No-ops (renders null) off a demo
                surface. */}
            <DemoBar demoBuild={isDemoBuild()} />
            {/* NA1-nav-loading: the route Suspense boundary lives HERE, inside
                the shell, so sidebar/chrome persist across every lazy chunk
                load (the study's two "page refresh" symptoms); routes get the
                destination family's shaped skeleton. */}
            <div className="flex flex-1 flex-col p-6">
              <Suspense fallback={<RouteFallback />}>
                {/* APL-12040 — logout guard: authStore.logout() nulls `user`
                    synchronously while Refine's auth-check query still reports
                    authenticated, so the mounted page tree re-rendered with
                    currentUser === null and any unguarded read threw into the
                    root AppErrorBoundary ("Something went wrong" flash on
                    logout — Common Assessment/Library/Users/Classes/Diagnostic
                    Tool; v3 threw identically but had no boundary, so it
                    flashed blank instead). `user == null` here is unambiguous
                    teardown, never boot: every authProvider.check() path that
                    resolves authenticated:true writes `user` first
                    (handleAuthSuccess → setAuthState), and the pre-resolve
                    window renders CustomAuthenticated's `loading` fallback,
                    not these children. Quiet skeleton until the logout
                    redirect lands. */}
                {currentUser ? <Outlet /> : <RouteFallback />}
              </Suspense>
            </div>
          </SidebarInset>
          {/* Global image zoom (IZ1-zoom): window-delegated `.show-html img`
              click → pan/zoom dialog, both migrated and v1-fallback routes.
              No claim needed — the v1 antd layer never mounts under AppShell
              (it lives in ExperienceLayout/EmbedLayout, separate route trees). */}
          <ZoomableImageLayer />
        </SidebarProvider>
      </Direction.Provider>
    </CustomAuthenticated>
  );
};
